import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field } from './Modal';
import Combobox from './Combobox';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { todayLocalISO } from '../utils/dates';
import {
  getJobTitles, saveJobTitle, getWorkTypes, saveWorkType, mergePresets,
} from '../utils/jobPresets';

/**
 * <NewJobModal /> — the single New job form used by the Jobs list, Overview and
 * Schedule. Job title and Work type are preset comboboxes (seeded values +
 * previously-used + add-new-that-saves). The Property picker loads the
 * contractor's real linked properties so the job is created against a real
 * property_id and persists to the database (returning a real job id).
 */
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const EMPTY = { title: '', property_id: '', property: '', client: '', workType: '', priority: 'medium', poNumber: '', preferredStart: '', noDate: false, scheduled: '', scheduled_time: '', scheduled_end_date: '', scheduled_end_time: '' };

const hintStyle = { fontSize: 'var(--cs-text-xs)', color: 'var(--cw-text-faint)' };
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-base)', color: 'var(--cw-text-2)', cursor: 'pointer', marginBottom: 10, minHeight: 44 };

/** Field-level validation. Returns { field: message }. */
export const validateJobForm = (form) => {
  const e = {};
  const title = String(form.title || '').trim();
  if (!title) e.title = 'Enter a job title';
  else if (title.length > 80) e.title = 'Job title must be 80 characters or fewer';
  if (!form.property_id) e.property_id = 'Choose a property for this job';
  if (!form.noDate) {
    const start = form.scheduled || todayLocalISO();
    if (form.scheduled_end_date && form.scheduled_end_date < start) e.scheduled_end_date = 'End date can’t be before the start date';
    if (!form.allDay && (form.scheduled_end_date || start) === start && form.scheduled_time && form.scheduled_end_time
      && form.scheduled_end_time <= form.scheduled_time) e.scheduled_end_time = 'Finish time must be after the start time';
  }
  return e;
};

const NewJobModal = ({ seenTitles = [], seenWorkTypes = [], initial = null, note = null, onClose, onCreated }) => {
  const [titles, setTitles] = useState(getJobTitles);
  const [workTypes, setWorkTypes] = useState(getWorkTypes);
  const [start] = useState(() => ({ ...EMPTY, allDay: !(initial && initial.scheduled_time), ...(initial || {}) }));
  const [form, setForm] = useState(start);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [propOptions, setPropOptions] = useState([]);
  const [localOptions, setLocalOptions] = useState([]);   // contractor-only properties (not on CloudSafe)
  const [propsState, setPropsState] = useState('loading'); // loading | ok | error
  const [propsReload, setPropsReload] = useState(0);
  const [buildingClients, setBuildingClients] = useState({ property: {}, local: {}, agents: { property: {}, local: {} } });
  // Property comes first; the client list then leads with that building's own
  // clients and managing agent, with every other client below for tenants and
  // other customers who aren't the building's main client.
  const [allClients, setAllClients] = useState([]);
  const selKind = String(form.property_id).startsWith('local-') ? 'local' : 'property';
  const selId = selKind === 'local' ? String(form.property_id).slice(6) : String(form.property_id);
  const buildingOwn = form.property_id ? (buildingClients[selKind]?.[selId] || []) : [];
  const buildingAgents = form.property_id ? (buildingClients.agents?.[selKind]?.[selId] || []) : [];
  const labelFor = (p) => (p.reference ? `${p.reference} · ${p.name}` : p.name);
  // "5 Wilton Road · Wilton London One Ltd, S2 Partnership" — duty holder first.
  const hintFor = (address, clients) => [address, (clients || []).join(', ')].filter(Boolean).join('  ·  ');
  const [eventTypeLabels, setEventTypeLabels] = useState([]); // configured calendar event types → bookable work types
  const [priorityOpts, setPriorityOpts] = useState(PRIORITIES); // DB-backed priorities, incl. custom ones from Settings

  const dirty = useMemo(() => Object.keys(form).some((k) => String(form[k] ?? '') !== String(start[k] ?? '')), [form, start]);

  // Load the contractor's real, linked properties (id + name + address) so the
  // job can be created against a real property_id. Falls back to /api/properties.
  useEffect(() => {
    let alive = true;
    setPropsState('loading');
    (async () => {
      let ok = true;
      let data;
      try { data = await apiFetch('/api/contractor/properties', { noCache: true }); }
      catch { try { data = await apiFetch('/api/properties'); } catch { data = []; ok = false; } }
      const list = Array.isArray(data) ? data : (data?.properties || data?.data || []);
      const mapped = list
        .map((p) => ({ id: p.id, name: p.name || p.title || p.address || `Property ${p.id}`, address: p.address || '', reference: p.reference || '' }))
        .filter((p) => p.id != null);
      // Contractor-only properties: selectable too — the job is raised against them
      // (property_id stays null) and links to the real property once CloudSafe approves.
      let locals = [];
      try {
        const d = await apiFetch('/api/contractor/local-properties', { noCache: true });
        const ll = Array.isArray(d?.properties) ? d.properties : [];
        locals = ll.map((p) => ({ id: p.id, name: p.name || `Property ${p.id}`, address: p.address || p.postcode || '', reference: p.reference || '' }));
      } catch { /* none */ }
      if (!alive) return;
      setPropOptions(mapped);
      setLocalOptions(locals);
      setPropsState(ok || locals.length ? 'ok' : 'error');
    })();
    return () => { alive = false; };
  }, [propsReload]);

  useEffect(() => {
    let alive = true;
    // Which clients each building is linked to, so the picker can tell apart two
    // records for the same site and show whose invoicing the job will carry.
    (async () => {
      try {
        const d = await apiFetch('/api/contractor/building-clients', { noCache: true });
        if (alive) setBuildingClients({ property: d?.property || {}, local: d?.local || {}, agents: d?.agents || { property: {}, local: {} } });
      } catch { /* hints just omit clients */ }
    })();
    // Calendar event types (Settings → Schedule) become bookable work-type options.
    (async () => {
      try {
        const s = await apiFetch('/api/contractor/settings');
        const ets = Array.isArray(s?.data?.scheduleEventTypes) ? s.data.scheduleEventTypes.map((et) => et.label).filter(Boolean) : [];
        if (alive) setEventTypeLabels(ets);
      } catch { /* optional extras — the presets still apply */ }
    })();
    // Priorities (Settings → Statuses & priorities) so custom ones are selectable here too.
    (async () => {
      try {
        const d = await apiFetch('/api/priorities');
        const list = Array.isArray(d?.priorities) ? d.priorities.map((p) => String(p.title || '').toLowerCase()).filter(Boolean) : [];
        if (alive && list.length) setPriorityOpts(Array.from(new Set([...PRIORITIES, ...list])));
      } catch { /* keep the built-in priorities */ }
    })();
    // Every client, not just those with linked buildings, so any client can be picked.
    (async () => {
      try {
        const d = await apiFetch('/api/contractor/clients');
        const list = Array.isArray(d?.clients) ? d.clients : (Array.isArray(d) ? d : []);
        if (alive) setAllClients(list.map((c) => c.name).filter(Boolean));
      } catch { /* agents only */ }
    })();
    // The company's shared standard list wins over this browser's presets.
    (async () => {
      try {
        const d = await apiFetch('/api/contractor/job-types');
        if (!alive) return;
        if (Array.isArray(d?.workTypes) && d.workTypes.length) setWorkTypes(d.workTypes);
        if (Array.isArray(d?.jobTitles) && d.jobTitles.length) setTitles(d.jobTitles);
      } catch { /* fall back to the local presets */ }
    })();
    return () => { alive = false; };
  }, []);

  const titleOptions = useMemo(() => mergePresets(titles, seenTitles), [titles, seenTitles]);
  const workTypeOptions = useMemo(() => mergePresets(workTypes, [...seenWorkTypes, ...eventTypeLabels]), [workTypes, seenWorkTypes, eventTypeLabels]);

  /** Add to the company list, surfacing the near-duplicate warning if there is one. */
  const addToList = async (kind, value, setter) => {
    try {
      const d = await apiFetch('/api/contractor/job-types', { method: 'POST', body: JSON.stringify({ kind, value }) });
      if (Array.isArray(d?.list)) setter(d.list);
    } catch (err) {
      const body = err?.body || {};
      if (body.nearDuplicate && body.existing) {
        toast.warn(body.message, { autoClose: 8000 });
      } else if (err?.status !== 409) {
        // The company list couldn't be updated — remember it for this user on this device only.
        setter(kind === 'workType' ? saveWorkType(value) : saveJobTitle(value));
      }
    }
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  const create = async () => {
    const errs = validateJobForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error('Check the highlighted fields'); return; }
    const title = form.title.trim();
    setSaving(true);
    // Register anything newly typed on the company list so the whole team gets it.
    addToList('jobTitle', title, setTitles);
    if (form.workType.trim()) addToList('workType', form.workType.trim(), setWorkTypes);
    try {
      const norm = (t) => (t && t.length === 5 ? `${t}:00` : t);
      // No date yet → unscheduled (Invited). A date → Scheduled (goes on the schedule).
      const startDate = form.noDate ? null : (form.scheduled || todayLocalISO());
      let scheduled_time = null;
      let scheduled_end = null;
      if (startDate) {
        if (form.allDay) {
          if (form.scheduled_end_date) scheduled_end = `${form.scheduled_end_date} 23:59:00`;
        } else {
          scheduled_time = norm(form.scheduled_time) || '09:00:00';
          const endDate = form.scheduled_end_date || startDate;
          const endTime = norm(form.scheduled_end_time) || '10:00:00';
          scheduled_end = `${endDate} ${endTime}`;
        }
      }
      const isLocalSel = String(form.property_id).startsWith('local-');
      const created = await apiFetch('/api/jobs', { method: 'POST', body: JSON.stringify({
        ...(isLocalSel ? { local_property_id: Number(String(form.property_id).slice(6)) } : { property_id: form.property_id }),
        title,
        area: form.workType.trim() || 'General',
        description: form.description?.trim() || null,
        scheduled_date: startDate,
        scheduled_time,
        scheduled_end,
        po_number: form.poNumber?.trim() || null,
        priority: form.priority || 'medium',
      }) });
      if (!created?.id) throw new Error('Create returned no id');
      // Preferred start is informational (Job details only, not the schedule) — save it to the job profile.
      let prefFailed = false;
      const profile = {
        ...(form.preferredStart ? { preferredStart: form.preferredStart } : {}),
        ...(form.client ? { client: form.client } : {}),
      };
      if (Object.keys(profile).length) {
        try { await apiFetch(`/api/contractor/jobs/${created.id}/job-profile`, { method: 'PUT', body: JSON.stringify({ data: profile }) }); }
        catch { prefFailed = true; }
      }
      toast.success(startDate ? 'Job created and scheduled' : 'Job created as an invite — no date yet');
      if (prefFailed) toast.warn('The client or preferred start wasn’t saved — set it on the job’s Details tab.');
      onCreated?.(form, created);
      onClose?.();
    } catch (e) {
      // Do NOT navigate to a phantom job — keep the modal open so it can be retried.
      toast.error(apiErrorMessage(e, 'Could not create the job — try again'));
      setSaving(false);
    }
  };

  const noProps = propsState === 'ok' && !propOptions.length && !localOptions.length;

  return (
    <Modal
      title="New job"
      maxWidth={480}
      onClose={onClose}
      dirty={dirty && !saving}
      footer={({ requestClose }) => (<>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
        <button type="button" className="cw-btn cw-btn-primary" onClick={create} disabled={saving} aria-busy={saving || undefined}>
          <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-plus'} aria-hidden="true" /> {saving ? 'Creating…' : 'Create job'}
        </button>
      </>)}
    >
      {note && (
        <div className="cw-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--cw-blue-tint)', borderRadius: 10, marginBottom: 14, fontSize: 'var(--cs-text-sm)', color: 'var(--cw-blue-pressed)' }}>
          <i className="fa-solid fa-calendar-day" aria-hidden="true" /> {note}
        </div>
      )}
      <Field
        label="Property"
        required
        error={errors.property_id || (propsState === 'error' ? 'Couldn’t load your properties.' : undefined)}
        hint={noProps ? 'No linked properties yet — request access from the Properties page first.'
          : (String(form.property_id).startsWith('local-') ? 'Contractor-only property — the job stays private to your company and links to CloudSafe once access is approved.' : undefined)}
      >
        {/* Searchable — an FM's list runs to hundreds of buildings. */}
        <Combobox
          value={form.property_id}
          placeholder={propsState === 'loading' ? 'Loading properties…' : 'Search properties by name or address…'}
          emptyText="No property matches that — add it from Properties first"
          options={[
            ...propOptions
              .map((p) => ({ value: String(p.id), label: labelFor(p), hint: hintFor(p.address, buildingClients.property[p.id]) })),
            ...localOptions.map((p) => ({
              value: `local-${p.id}`, label: labelFor(p), hint: hintFor(p.address, buildingClients.local[p.id]),
              group: 'Contractor-only (not on CloudSafe)',
            })),
          ]}
          onChange={(id) => {
            const p = propOptions.find((x) => String(x.id) === String(id))
              || localOptions.find((x) => `local-${x.id}` === String(id));
            // Default the client to the building's main client; it can still be changed.
            const kind = String(id).startsWith('local-') ? 'local' : 'property';
            const key = kind === 'local' ? String(id).slice(6) : String(id);
            const main = (buildingClients[kind]?.[key] || [])[0] || '';
            setForm((f) => ({ ...f, property_id: id, property: p?.name || '', client: main }));
            if (errors.property_id) setErrors((e) => { const n = { ...e }; delete n.property_id; return n; });
          }}
        />
      </Field>
      {propsState === 'error' && (
        <button type="button" className="cw-btn-link" style={{ margin: '-6px 0 10px' }} onClick={() => setPropsReload((n) => n + 1)}>
          <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Retry loading properties
        </button>
      )}

      {form.property_id && (
        <Field label="Client" hint="Defaults to the building's main client — pick another if the job is for someone else.">
          <select value={form.client || ''} onChange={(e) => set('client', e.target.value)}>
            <option value="">Building's main client</option>
            {(buildingOwn.length || buildingAgents.length) && (
              <optgroup label="This building">
                {[...new Set([...buildingOwn, ...buildingAgents])].map((c) => <option key={`b-${c}`} value={c}>{c}</option>)}
              </optgroup>
            )}
            <optgroup label="Other clients">
              {allClients.filter((c) => !buildingOwn.includes(c) && !buildingAgents.includes(c))
                .sort((x, y) => x.localeCompare(y)).map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>
        </Field>
      )}

      <Field label="Job title" required error={errors.title} hint={`${form.title.length}/80 characters`}>
        <Combobox
          value={form.title}
          onChange={(v) => set('title', v)}
          options={titleOptions}
          placeholder="Choose or add a job title…"
          onAddNew={(v) => addToList('jobTitle', v, setTitles)}
        />
      </Field>


      <Field label="Work type">
        <Combobox
          value={form.workType}
          onChange={(v) => set('workType', v)}
          options={workTypeOptions}
          placeholder="Choose or add a work type…"
          onAddNew={(v) => addToList('workType', v, setWorkTypes)}
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 12 }}>
        <Field label="Priority">
          <select value={form.priority} onChange={(e) => set('priority', e.target.value)}>
            {priorityOpts.map((p) => <option key={p} value={p}>{cap(p)}</option>)}
          </select>
        </Field>
        <Field label="PO number (optional)">
          <input value={form.poNumber} onChange={(e) => set('poNumber', e.target.value)} placeholder="Client's purchase-order ref" />
        </Field>
      </div>

      <Field label="Preferred start (optional)" hint="A requested start date — shows on the job details only, not the schedule.">
        <input type="date" value={form.preferredStart} onChange={(e) => set('preferredStart', e.target.value)} />
      </Field>

      <fieldset style={{ border: 0, padding: 0, margin: '10px 0 0' }}>
        <legend className="cw-label" style={{ marginBottom: 6 }}>Schedule</legend>
        <label style={checkRow}>
          <input type="checkbox" checked={!!form.noDate} onChange={(e) => set('noDate', e.target.checked)} />
          No date yet <span style={hintStyle}>(create as an invite — schedule it later)</span>
        </label>
        {!form.noDate && (
          <>
            <label style={checkRow}>
              <input type="checkbox" checked={!!form.allDay} onChange={(e) => set('allDay', e.target.checked)} />
              All day <span style={hintStyle}>(no set time — shows in the all-day row)</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 12 }}>
              <Field label="Start date" hint={form.scheduled ? undefined : 'Defaults to today'}>
                <input type="date" value={form.scheduled} onChange={(e) => set('scheduled', e.target.value)} />
              </Field>
              {!form.allDay && (
                <Field label="Start time">
                  <input type="time" value={form.scheduled_time} onChange={(e) => set('scheduled_time', e.target.value)} />
                </Field>
              )}
              <Field label={form.allDay ? 'End date (optional)' : 'End date'} error={errors.scheduled_end_date}>
                <input type="date" value={form.scheduled_end_date} min={form.scheduled || undefined} onChange={(e) => set('scheduled_end_date', e.target.value)} />
              </Field>
              {!form.allDay && (
                <Field label="Finish time" error={errors.scheduled_end_time}>
                  <input type="time" value={form.scheduled_end_time} onChange={(e) => set('scheduled_end_time', e.target.value)} />
                </Field>
              )}
            </div>
          </>
        )}
      </fieldset>

      <p style={{ ...hintStyle, margin: '4px 0 0' }}>
        {form.noDate
          ? 'The job is raised as an invite and appears at the top of your list.'
          : 'The job is created as scheduled and appears on the schedule.'}
      </p>
    </Modal>
  );
};

export default NewJobModal;
